/**
 * Builds the plugin-extensions registry for the lesscommerce admin.
 *
 * Webpack equivalent of lesscms's Vite-based scanner — same shape and
 * contract, different glob mechanism. Scans `src/plugin-extensions/<id>/`
 * for `manifest.json` and Vue components, and returns the registry that
 * `main.js` provides to the Vue app via `PLUGIN_EXTENSIONS_KEY`.
 *
 * Convention: the directory is symlinked from
 *   lesscommerce/be/plugins/<id>/Frontend/Admin/
 * so plugin source lives with the plugin code, not in fe/.
 *
 * Manifest contract (per plugin, in `<id>/manifest.json`):
 *
 *   {
 *     "id": "photo-albums",
 *     "slots": {
 *       "order.detail": [
 *         { "key": "albums", "label": "Albumy", "component": "Slots/OrderDetailAlbums.vue" }
 *       ]
 *     }
 *   }
 *
 * Components are referenced by path relative to the plugin's Admin
 * directory and loaded lazily so plugin UI doesn't ship until needed.
 */

// Manifests: eager (need them synchronously to build the registry).
// Webpack 5's require.context returns a `keys()` list and a callable that
// resolves modules. For JSON, default export is the parsed value.
const manifestCtx = require.context("./", true, /\/manifest\.json$/);

// Components: lazy mode — `componentCtx(key)` returns a Promise<Module>.
// Each matching .vue file becomes its own webpack chunk, loaded on demand
// when the slot is actually rendered.
const componentCtx = require.context("./", true, /\.vue$/, "lazy");

function buildRegistry() {
  const pages = [];
  const menu = [];
  const slots = {};

  for (const manifestPath of manifestCtx.keys()) {
    // Webpack JSON loader is inconsistent about default export shape across
    // versions and project configs — accept both `{...}` and `{ default: {...} }`.
    const raw = manifestCtx(manifestPath);
    const manifest = raw?.default ?? raw;
    const pluginId = manifest?.id;
    if (!pluginId) continue;

    // './photo-albums/manifest.json' → './photo-albums/'
    const manifestDir = manifestPath.replace(/manifest\.json$/, "");

    const resolveLoader = (componentPath) => {
      const key = manifestDir + componentPath;
      if (!componentCtx.keys().includes(key)) {
        if (typeof console !== "undefined") {
          console.warn(
            `[plugin-extensions] ${pluginId}: component not found: ${key}`
          );
        }
        return null;
      }
      return async () => {
        const mod = await componentCtx(key);
        return mod.default || mod;
      };
    };

    for (const page of manifest.pages || []) {
      // Accept both `path` (router convention) and `route` (legacy) for the URL.
      const path = page?.path || page?.route;
      if (!path || !page?.component) continue;
      const loader = resolveLoader(page.component);
      if (!loader) continue;
      pages.push({
        pluginId,
        path,
        name: page.name || `plugin.${pluginId}.${path}`,
        title: page.title || null,
        loader,
      });
    }

    for (const entry of manifest.menu || []) {
      if (!entry?.link || !entry?.label) continue;
      menu.push({
        pluginId,
        id: entry.id || `plugin-${pluginId}-${entry.link}`,
        label: entry.label,
        icon: entry.icon || null,
        link: entry.link,
        section: entry.section || "plugins",
      });
    }

    for (const [slotName, entries] of Object.entries(manifest.slots || {})) {
      for (const entry of entries) {
        if (!entry?.key || !entry?.component) continue;
        const loader = resolveLoader(entry.component);
        if (!loader) continue;
        slots[slotName] = slots[slotName] || [];
        slots[slotName].push({
          pluginId,
          key: entry.key,
          label: entry.label,
          loader,
        });
      }
    }
  }

  return { pages, menu, slots };
}

export default buildRegistry;
